import {RenderRule, ResourceType} from "./types";
import {isValidFileType} from "./contentUtils";
import {Image} from "./components/image/Image";
import {AutoLinkNone} from "./components/autolink/AutoLinkNone";
import {AutoLink} from "./components/autolink/AutoLink";
import {File} from "./components/file/file";
import {PanelLink} from "./components/panel-link/PanelLink";
import {ExmarkLink} from "./components/exmark-link/ExmarkLink";
import {AutoLinkNav} from "./components/autolink-nav/AutoLinkNav";
import {JumpLink} from "./components/jump-link/JumpLink";
import {Xbox} from "./components/xbox/Xbox";
import {Table} from "./components/table/Table";
import {Booktags} from "./components/booktags/Booktags";
import {XFootnote} from "./components/x-footnote/XFootnote";
import {ExternalLink} from "./components/external-link/ExternalLink";
import pageBreakClasses from "./components/page-break/pageBreak.module.css";

export const ruleset: RenderRule[] = [
    // Image
    // auto-link-none
    // Kind: auto-link, object.type: linkdata, og har children -> AutoLink
    // Kind: preview, object.type: linkdata, og har children -> PreviewLink (hva er forskjellen?)
    // Kind: nav, object.type: linkdata, isValidFileType -> FileRef
    // Kind: "ilink nav" || "nav", object.type: linkdata, children.length > 0 -> AutoLinkNav
    // Kind: "auto-link-nav" || "nav", object.type: linkdata, children.length > 0 -> AutoLinkNav
    //       Konkat disse to.
    // Kind: "ilink definition" || (kind: "ilink" && node.element.attributes.class: "ilink definition") || node.element.attributes.class: "ilink" -> IfrsDefLink
    // Kind: "ilink nav", element.attributes["data-segment"] !== undefined, element.attributes["data-id"] !== undefined -> JumpLink
    // Kind: ("ilink footnote" || "ilink footnote edu"), element.attributes["data-segment"] !== undefined, element.attributes["data-id"] !== undefined -> IfrsFootnote
    // Kind: "ilink preview exmark", element.attributes["data-id"] !== undefined -> ExmarkLink
    // Kind: "href", element.attributes.class: "href", children.length > 0, object.data.href.length > 0 -> ExternalHref
    // Kind: "xbox" || "xbox open" -> Xbox
    // Element.name: "table", topicData.resourceTypeId !== documentgenerator && topicData.resourceTypeId !== checklist -> Table
    // Kind: booktags -> Booktags

    // Dropp denne, men HUSK HVORFOR VI HAR DEN. Den brukes kun til å rendre "related" i LEGACY sjekklister:
    // Kind: related, options.sliderRelation -> RelatedLink
    // Dropp denne, men HUSK HVORFOR VI HAR DEN  Den brukes kun til å rendre "related" i LEGACY sjekklister:
    // Kind: related, object.data.topicId === undefined OG object.data.categoryId er enten INDEX, SOURCES eller TOOLS -> RelatedLink

    // Kind: dib-x-footnote, element.attributes["data-title"] !== undefined, element.attributes.id !== undefined -> XFootnte
    // Kind: dib-x-pagebreak -> PageBreak

    // Total:
    // - Image
    // - AutoLinkNone
    // - AutoLink
    // - PreviewLink
    // - FileRef
    // - AutoLinkNav
    // - IfrsDefLink
    // - IfrsFootnote
    // - JumpLink
    // - ExmarkLink
    // - ExternalHref
    // - Xbox
    // - Table
    // - Booktags
    // - XFootnote
    // - PageBreak
    {
        shouldParse: (node) => node.kind === "image",
        parser: (node) => (
            <Image node={node} key={node.element?.attributes?.id} />
        ),
    },
    {
        shouldParse: (node) => node.kind === "auto-link-none",
        parser: (node, options, data) => (
            <AutoLinkNone
                children={data?.children ?? []}
                key={node.element?.attributes?.id}
            />
        ),
    },
    {
        // Auto-link
        shouldParse: (node) => {
            return !!(
                node.kind === "auto-link" &&
                node.object?.type === "linkdata" &&
                node?.element?.children?.length &&
                node?.element?.children?.length > 0
            );
        },
        parser: (node, options, data) => {
            return (
                <AutoLink
                    rootTopicId={options.rootTopicId}
                    children={data?.children ?? []}
                    node={node}
                    key={node.element?.attributes?.id}
                />
            );
        },
    },
    {
        // Preview link
        shouldParse: (node) => {
            return !!(
                node.kind === "preview" &&
                node.object?.type === "linkdata" &&
                node?.element?.children?.length &&
                node?.element?.children?.length > 0
            );
        },
        parser: (node, options, data) => {
            return (
                <AutoLink
                    children={data?.children ?? []}
                    node={node}
                    key={node.element?.attributes?.id}
                />
            );
        },
    },
    {
        // File reference
        shouldParse: (node) => {
            return !!(
                node.kind === "nav" &&
                node.object?.type === "linkdata" &&
                isValidFileType(
                    node.object.data.resourceTypeId as unknown as ResourceType
                )
            );
        },
        parser: (node, options, data) => {
            return (
                <File
                    children={data?.children ?? []}
                    node={node}
                    key={node.element?.attributes?.id}
                />
            );
        },
    },
    {
        // JumpLink – more specific "ilink nav" (with segment+id), must precede AutoLinkNav
        shouldParse: (node) =>
            !!(
                node.kind === "ilink nav" &&
                node.element?.attributes?.["data-segment"] !== undefined &&
                node.element?.attributes?.["data-id"] !== undefined
            ),
        parser: (node, _options, data) => (
            <JumpLink
                node={node}
                map={data!.map!}
                children={data?.children ?? []}
                key={node.element?.attributes?.id}
            />
        ),
    },
    {
        // AutoLinkNav – "ilink nav" | "nav" | "auto-link-nav" with linkdata children
        shouldParse: (node) =>
            !!(
                (node.kind === "ilink nav" ||
                    node.kind === "nav" ||
                    node.kind === "auto-link-nav") &&
                node.object?.type === "linkdata" &&
                node.element?.children?.length &&
                node.element.children.length > 0
            ),
        parser: (node, _options, data) => (
            <AutoLinkNav
                node={node}
                children={data?.children ?? []}
                key={node.element?.attributes?.id}
            />
        ),
    },
    {
        // IfrsDefLink
        shouldParse: (node) =>
            !!(
                node.kind === "ilink definition" ||
                (node.kind === "ilink" &&
                    node.element?.attributes?.class === "ilink definition") ||
                node.element?.attributes?.class === "ilink"
            ),
        parser: (node, _options, data) => (
            <PanelLink
                node={node}
                map={data!.map!}
                children={data?.children ?? []}
                variant="definition"
                key={node.element?.attributes?.id}
            />
        ),
    },
    {
        // IfrsFootnote – "ilink footnote" | "ilink footnote edu" with segment+id
        shouldParse: (node) =>
            !!(
                (node.kind === "ilink footnote" ||
                    node.kind === "ilink footnote edu") &&
                node.element?.attributes?.["data-segment"] !== undefined &&
                node.element?.attributes?.["data-id"] !== undefined
            ),
        parser: (node, _options, data) => (
            <PanelLink
                node={node}
                map={data!.map!}
                children={data?.children ?? []}
                variant={
                    node.kind === "ilink footnote edu"
                        ? "footnote-edu"
                        : "footnote"
                }
                key={node.element?.attributes?.id}
            />
        ),
    },
    {
        // ExmarkLink
        shouldParse: (node) =>
            !!(
                node.kind === "ilink preview exmark" &&
                node.element?.attributes?.["data-id"] !== undefined
            ),
        parser: (node, _options, data) => (
            <ExmarkLink
                node={node}
                map={data!.map!}
                key={node.element?.attributes?.id}
            />
        ),
    },
    {
        // ExternalHref
        shouldParse: (node) =>
            !!(
                node.kind === "href" &&
                node.element?.attributes?.class === "href" &&
                node.element?.children?.length &&
                node.element.children.length > 0 &&
                node.object?.type === "linkdata" &&
                node.object.data.href &&
                node.object.data.href.length > 0
            ),
        parser: (node, _options, data, key) => (
            <ExternalLink node={node} key={key}>
                {data?.children ?? []}
            </ExternalLink>
        ),
    },
    {
        // Xbox
        shouldParse: (node) =>
            node.kind === "xbox" || node.kind === "xbox open",
        parser: (node, _options, data, key) => (
            <Xbox
                node={node}
                map={data!.map!}
                children={data?.children ?? []}
                key={key}
            />
        ),
    },
    {
        // Table – not documentGenerator, not checklist
        shouldParse: (node, map) =>
            !!(
                node.element?.name === "table" &&
                map.resourceTypeId !== ResourceType.documentGenerator &&
                map.resourceTypeId !== ResourceType.checklist
            ),
        parser: (node, _options, data, key) => (
            <Table
                node={node}
                children={data?.children ?? []}
                key={key}
            />
        ),
    },
    {
        // Booktags
        shouldParse: (node) => node.kind === "booktags",
        parser: (node, _options, data, key) => (
            <Booktags
                node={node}
                children={data?.children ?? []}
                key={key}
            />
        ),
    },
    {
        // XFootnote
        shouldParse: (node) =>
            !!(
                node.kind === "dib-x-footnote" &&
                node.element?.attributes?.["data-title"] !== undefined &&
                node.element?.attributes?.id !== undefined
            ),
        parser: (node, _options, _data, key) => (
            <XFootnote node={node} key={key} />
        ),
    },
    {
        // PageBreak
        shouldParse: (node) => node.kind === "dib-x-pagebreak",
        parser: (_node, _options, _data, key) => (
            <div className={pageBreakClasses.pageBreak} key={key} />
        ),
    },

    // This always go last:
    {
        shouldParse: (_: any) => true,
        parser: (_: any) => null,
    },
];
